import React, { useState, useEffect, useCallback } from 'react';
import { 
  MapPin, Search, Plus, Filter, 
  Layers, Edit3, 
  Trash2, AlertCircle, CheckCircle2, Loader2, X
} from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components';
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal';
import { locationService } from '../../../services/erpApi';
import './LocationsManagerPage.css';

interface Location {
  id: number;
  code: string;
  zone: string;
  aisle: string;
  rack: string;
  level: string;
  position: string;
  type: 'picking' | 'pulmao' | 'doca';
  status: 'active' | 'inactive' | 'full';
  capacity: number;
  occupancy: number;
  created_at?: string;
  updated_at?: string;
}

const LocationsManagerPage: React.FC = () => {
  const { toast, showToast, hideToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState<Partial<Location>>({
    code: '',
    zone: '',
    aisle: '',
    rack: '',
    level: '',
    position: '',
    type: 'picking',
    status: 'active',
    capacity: 100,
    occupancy: 0
  });

  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await locationService.getAll({ zone: zoneFilter !== 'all' ? zoneFilter : undefined });
      if (response.success) {
        setLocations(response.data || []);
      } else {
        showToast(response.message || 'Erro ao carregar endereços', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao carregar endereços:', error);
      showToast(error.response?.data?.message || 'Erro ao carregar endereços', 'error');
    } finally {
      setLoading(false);
    }
  }, [zoneFilter, showToast]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const filteredLocations = locations.filter(loc => {
    const matchesSearch = loc.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesZone = zoneFilter === 'all' || loc.zone === zoneFilter;
    return matchesSearch && matchesZone;
  });

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'active': return 'locations-status-active';
      case 'full': return 'locations-status-full';
      case 'inactive': return 'locations-status-inactive';
      default: return 'locations-status-inactive';
    }
  };

  const getTypeStyle = (type: string) => {
    switch (type) {
      case 'picking': return 'locations-type-picking';
      case 'pulmao': return 'locations-type-pulmao';
      case 'doca': return 'locations-type-doca';
      default: return 'locations-type-default';
    }
  };

  const handleCreate = () => {
    setFormData({
      code: '',
      zone: '',
      aisle: '',
      rack: '',
      level: '',
      position: '',
      type: 'picking',
      status: 'active',
      capacity: 100,
      occupancy: 0
    });
    setShowCreateModal(true);
  };

  const handleEdit = (location: Location) => {
    setSelectedLocation(location);
    setFormData(location);
    setShowEditModal(true);
  };

  const handleDelete = (location: Location) => {
    setSelectedLocation(location);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    try {
      if (showCreateModal) {
        const response = await locationService.create(formData);
        if (response.success) {
          showToast('Endereço criado com sucesso!', 'success');
          setShowCreateModal(false);
          await loadLocations();
        } else {
          showToast(response.message || 'Erro ao criar endereço', 'error');
        }
      } else if (showEditModal && selectedLocation) {
        const response = await locationService.update(selectedLocation.id, formData);
        if (response.success) {
          showToast('Endereço atualizado com sucesso!', 'success');
          setShowEditModal(false);
          setSelectedLocation(null);
          await loadLocations();
        } else {
          showToast(response.message || 'Erro ao atualizar endereço', 'error');
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar endereço:', error);
      showToast(error.response?.data?.message || 'Erro ao salvar endereço', 'error');
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedLocation) return;
    
    try {
      const response = await locationService.delete(selectedLocation.id);
      if (response.success) {
        showToast('Endereço excluído com sucesso!', 'success');
        setShowDeleteModal(false);
        setSelectedLocation(null);
        await loadLocations();
      } else {
        showToast(response.message || 'Erro ao excluir endereço', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao excluir endereço:', error);
      showToast(error.response?.data?.message || 'Erro ao excluir endereço', 'error');
    }
  };

  // Obter zonas únicas para filtro
  const zones = ['all', ...Array.from(new Set(locations.map(l => l.zone)))];

  // Calcular estatísticas
  const totalLocations = locations.length;
  const activeLocations = locations.filter(l => l.status === 'active').length;
  const fullLocations = locations.filter(l => l.status === 'full').length;
  const avgOccupancy = locations.length > 0
    ? Math.round(locations.reduce((acc, loc) => acc + (loc.occupancy / loc.capacity), 0) / locations.length * 100)
    : 0;

  if (loading) {
    return (
      <div className="locations-loading">
        <Loader2 size={32} className="locations-spinner" />
        <p>Carregando endereços...</p>
      </div>
    );
  }

  return (
    <div className="locations-manager">
      {/* Header */}
      <div className="locations-header">
        <div>
          <h1 className="locations-title">
            Gestão de <span className="locations-title-accent">Endereços</span>
          </h1>
          <p className="locations-subtitle">
            Mapeamento Físico e Capacidade do Armazém
          </p>
        </div>
        <div className="locations-header-actions">
          <button className="locations-btn-secondary">
            <Filter size={16} /> Filtros
          </button>
          <button className="locations-btn-primary" onClick={handleCreate}>
            <Plus size={16} /> Novo Endereço
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="locations-stats">
        <div className="locations-stat-card">
          <div className="locations-stat-icon locations-stat-icon-primary">
            <MapPin size={20} />
          </div>
          <p className="locations-stat-label">Total de Endereços</p>
          <p className="locations-stat-value">{totalLocations}</p>
        </div>
        <div className="locations-stat-card">
          <div className="locations-stat-icon locations-stat-icon-emerald">
            <CheckCircle2 size={20} />
          </div>
          <p className="locations-stat-label">Endereços Livres</p>
          <p className="locations-stat-value">{activeLocations}</p>
        </div>
        <div className="locations-stat-card">
          <div className="locations-stat-icon locations-stat-icon-rose">
            <AlertCircle size={20} />
          </div>
          <p className="locations-stat-label">Endereços Cheios</p>
          <p className="locations-stat-value">{fullLocations}</p>
        </div>
        <div className="locations-stat-card">
          <div className="locations-stat-icon locations-stat-icon-blue">
            <Layers size={20} />
          </div>
          <p className="locations-stat-label">Ocupação Média</p>
          <p className="locations-stat-value">{avgOccupancy}%</p>
        </div>
      </div>

      {/* Main Container */}
      <div className="locations-card">
        {/* Active Filters & Search */}
        <div className="locations-filters">
          <div className="locations-filters-content">
            <div className="locations-search-wrapper">
              <Search className="locations-search-icon" size={16} />
              <input 
                type="text" 
                placeholder="Buscar por código do endereço..." 
                className="locations-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="locations-zone-filters">
              <span className="locations-zone-label">Zona:</span>
              {zones.map((z) => (
                <button
                  key={z}
                  onClick={() => setZoneFilter(z)}
                  className={`locations-zone-btn ${zoneFilter === z ? 'locations-zone-btn-active' : ''}`}
                >
                  {z === 'all' ? 'Todas' : `Zona ${z}`}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="locations-table-wrapper">
          <table className="locations-table">
            <thead>
              <tr>
                <th>Código / Tipo</th>
                <th>Estrutura (Z-R-N-P)</th>
                <th className="text-center">Ocupação</th>
                <th className="text-center">Status</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocations.length === 0 ? (
                <tr>
                  <td colSpan={5} className="locations-empty">
                    <div className="locations-empty-content">
                      <MapPin size={48} />
                      <p>Nenhum endereço encontrado</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredLocations.map((loc) => (
                  <tr key={loc.id} className="locations-table-row">
                    <td>
                      <div className="locations-code-info">
                        <p className="locations-code">{loc.code}</p>
                        <span className={`locations-type-badge ${getTypeStyle(loc.type)}`}>
                          {loc.type === 'picking' ? 'Picking' : loc.type === 'pulmao' ? 'Pulmão' : 'Doca'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="locations-structure">
                        <span className="locations-structure-item">{loc.zone}</span> -
                        <span className="locations-structure-item">{loc.rack}</span> -
                        <span className="locations-structure-item">{loc.level}</span> -
                        <span className="locations-structure-item">{loc.position}</span>
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="locations-occupancy">
                        <span className="locations-occupancy-percent">
                          {Math.round((loc.occupancy / loc.capacity) * 100)}%
                        </span>
                        <div className="locations-occupancy-bar">
                          <div 
                            className={`locations-occupancy-fill ${loc.occupancy === loc.capacity ? 'locations-occupancy-full' : ''}`}
                            style={{ width: `${(loc.occupancy / loc.capacity) * 100}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      <span className={`locations-status-badge ${getStatusStyle(loc.status)}`}>
                        {loc.status === 'active' ? 'Livre' : loc.status === 'full' ? 'Cheio' : 'Inativo'}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="locations-actions">
                        <button 
                          className="locations-action-btn locations-action-edit"
                          onClick={() => handleEdit(loc)}
                          title="Editar endereço"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button 
                          className="locations-action-btn locations-action-delete"
                          onClick={() => handleDelete(loc)}
                          title="Excluir endereço"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Criar/Editar */}
      {(showCreateModal || showEditModal) && (
        <div className="locations-modal-overlay" onClick={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedLocation(null);
        }}>
          <div className="locations-modal" onClick={(e) => e.stopPropagation()}>
            <div className="locations-modal-header">
              <h3>{showCreateModal ? 'Novo Endereço' : 'Editar Endereço'}</h3>
              <button 
                className="locations-modal-close"
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedLocation(null);
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="locations-modal-content">
              <div className="locations-form-group">
                <label>Código *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Ex: A1-01-A"
                />
              </div>

              <div className="locations-form-row">
                <div className="locations-form-group">
                  <label>Zona *</label>
                  <input
                    type="text"
                    value={formData.zone}
                    onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                    placeholder="Ex: A"
                    maxLength={1}
                  />
                </div>
                <div className="locations-form-group">
                  <label>Corredor</label>
                  <input
                    type="text"
                    value={formData.aisle}
                    onChange={(e) => setFormData({ ...formData, aisle: e.target.value })}
                    placeholder="Ex: 1"
                  />
                </div>
                <div className="locations-form-group">
                  <label>Prateleira</label>
                  <input
                    type="text"
                    value={formData.rack}
                    onChange={(e) => setFormData({ ...formData, rack: e.target.value })}
                    placeholder="Ex: 01"
                  />
                </div>
                <div className="locations-form-group">
                  <label>Nível</label>
                  <input
                    type="text"
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    placeholder="Ex: 1"
                  />
                </div>
                <div className="locations-form-group">
                  <label>Posição</label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    placeholder="Ex: A"
                    maxLength={1}
                  />
                </div>
              </div>

              <div className="locations-form-row">
                <div className="locations-form-group">
                  <label>Tipo *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'picking' | 'pulmao' | 'doca' })}
                  >
                    <option value="picking">Picking</option>
                    <option value="pulmao">Pulmão</option>
                    <option value="doca">Doca</option>
                  </select>
                </div>
                <div className="locations-form-group">
                  <label>Status *</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' | 'full' })}
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                    <option value="full">Cheio</option>
                  </select>
                </div>
                <div className="locations-form-group">
                  <label>Capacidade *</label>
                  <input
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                    placeholder="100"
                    min="0"
                  />
                </div>
                <div className="locations-form-group">
                  <label>Ocupação</label>
                  <input
                    type="number"
                    value={formData.occupancy}
                    onChange={(e) => setFormData({ ...formData, occupancy: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>

              <div className="locations-modal-actions">
                <button
                  className="locations-modal-btn-secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedLocation(null);
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="locations-modal-btn-primary"
                  onClick={handleSave}
                  disabled={!formData.code || !formData.zone}
                >
                  {showCreateModal ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && selectedLocation && (
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedLocation(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Excluir Endereço"
          message={`Tem certeza que deseja excluir o endereço "${selectedLocation.code}"? Esta ação não pode ser desfeita.`}
          type="danger"
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      )}

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default LocationsManagerPage;
